import { useState } from 'react';
import styled from 'styled-components';
import { Modal } from 'antd';
import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import DefaultButton from 'components/button/DefaultButton';
import DOMPurify from 'dompurify';
import { Line } from 'components/join/Step1';

interface UnsubscribeModalProps {
  open: boolean;
  onCancel: () => void;
  boardSearchList: BoardSearchList[];
  currentIndex: number;
}

const NoticeDetailModal = ({
  onCancel,
  open,
  boardSearchList,
  currentIndex
}: UnsubscribeModalProps) => {
  const [currentModalIndex, setCurrentModalIndex] = useState<number>(currentIndex);

  const handleClickNextContents = () => {
    if (currentModalIndex < boardSearchList.length - 1) {
      setCurrentModalIndex(currentModalIndex + 1);
    }
  };

  const handleClickPrevContents = () => {
    if (currentModalIndex > 0) {
      setCurrentModalIndex(currentModalIndex - 1);
    }
  };

  const labels = (submenuidx: number) => {
    switch (submenuidx) {
      case 41:
        return {
          color: '#FE7C04',
          text: '공지'
        };
      case 42:
        return {
          color: '#53D63E',
          text: '업데이트'
        };
      case 43:
        return {
          color: '#9E9E9E',
          text: '휴일안내'
        };
      default:
        return {
          color: '#FE7C04',
          text: '공지'
        };
    }
  };

  const currentBoard = boardSearchList[currentModalIndex];
  const labelInfo = labels(boardSearchList[currentModalIndex].submenuidx);
  const isFirstIndex = currentModalIndex === 0;
  const isLastIndex = currentModalIndex === boardSearchList.length - 1;

  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered width={720}>
      <Wrapper>
        <Label $labelColor={labelInfo.color}>{labelInfo.text}</Label>
        <Title>{currentBoard.title}</Title>
        <Date>{currentBoard.updatedate}</Date>
        <Line style={{ margin: 0 }} />
        <ContentsWrapper className="wrapper">
          {currentBoard.contents && (
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentBoard.contents) }} />
          )}
        </ContentsWrapper>
        <Line style={{ margin: 0 }} />
        <ButtonWrapper>
          <DefaultButton onClick={handleClickPrevContents} text="이전글" disabled={isFirstIndex} />
          <DefaultButton onClick={handleClickNextContents} text="다음글" disabled={isLastIndex} />
        </ButtonWrapper>
      </Wrapper>
    </Modal>
  );
};

export default NoticeDetailModal;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h4`
  color: ${color.black2};
  ${font.h3_bold};
  text-align: left;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  p {
    text-align: center;
  }

  p button.ant-btn {
    width: 160px;
    color: ${color.black2};
    ${font.body_medium};
  }

  & p:first-of-type button {
    color: ${color.gray500};
  }
`;

const Date = styled.p`
  color: ${color.gray600};
  ${font.caption1_regular};
`;

const ContentsWrapper = styled.div`
  width: 100%;
  min-height: max-content;
  max-height: 602px;
  color: ${color.gray600};
  ${font.caption1_regular};
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  & img {
    width: 100%;
    height: 100%;
  }
`;

const Label = styled.div<{ $labelColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 4px;
  background: ${({ $labelColor }) => $labelColor};
  color: ${color.white2};
  text-align: center;
  ${font.caption2_medium};
`;
